// Hand-rolled YAML tokenizer for the in-app prompt viewer. Output is a
// list of styled spans rendered into a <pre>. Zero external deps,
// works in Cloudflare Pages cold start. Good enough for prompt YAML —
// not a replacement for shiki / monaco at a general scale.

export type TokenKind =
  | "key"
  | "string"
  | "number"
  | "bool"
  | "null"
  | "comment"
  | "anchor"
  | "punct"
  | "directive"
  | "indent"
  | "text";

export interface Token { kind: TokenKind; text: string }

const KEY = /^(\s*)([-A-Za-z_][-A-Za-z0-9_]*)\s*:/;
const COMMENT = /^(\s*)(#.*)$/;
const DIRECTIVE = /^(%\S+.*)$/;
const STRING_D = /^"(?:[^"\\]|\\.)*"/;
const STRING_S = /^'(?:[^'\\]|\\.)*'/;
const NUMBER = /^-?\d+(?:\.\d+)?\b/;
const BOOL = /^(?:true|false|on|off|yes|no)\b/i;
const NULL = /^(?:null|~)\b/i;
const PUNCT = /^[{}\[\],&*|>:?-]/;

export function tokenizeYamlLine(line: string): Token[] {
  if (line.length === 0) return [{ kind: "text", text: "" }];
  const c = line.match(COMMENT);
  if (c) {
    return [
      { kind: "indent", text: c[1] ?? "" },
      { kind: "comment", text: c[2] ?? "" },
    ];
  }
  const d = line.match(DIRECTIVE);
  if (d) return [{ kind: "directive", text: line }];

  const out: Token[] = [];
  let i = 0;
  // Detect leading list "- "
  const indMatch = line.match(/^\s*/);
  const indent = indMatch ? indMatch[0] : "";
  if (indent) out.push({ kind: "indent", text: indent });
  i = indent.length;

  // Detect "- " list marker.
  if (line.startsWith("- ", i)) {
    out.push({ kind: "punct", text: "- " });
    i += 2;
  }

  // Detect a leading key.
  const keyMatch = line.slice(i).match(KEY);
  if (keyMatch && (keyMatch[1] ?? "").length === 0) {
    out.push({ kind: "key", text: keyMatch[2] ?? "" });
    out.push({ kind: "punct", text: ":" });
    i += (keyMatch[2] ?? "").length + 1;
  }

  // Tokenize the rest of the line, value-side.
  while (i < line.length) {
    const rest = line.slice(i);
    if (rest.startsWith(" ")) {
      out.push({ kind: "text", text: " " });
      i += 1;
      continue;
    }
    let m: RegExpExecArray | null;
    if ((m = STRING_D.exec(rest))) { out.push({ kind: "string", text: m[0] }); i += m[0].length; continue; }
    if ((m = STRING_S.exec(rest))) { out.push({ kind: "string", text: m[0] }); i += m[0].length; continue; }
    if ((m = NUMBER.exec(rest)))   { out.push({ kind: "number", text: m[0] }); i += m[0].length; continue; }
    if ((m = BOOL.exec(rest)))     { out.push({ kind: "bool",   text: m[0] }); i += m[0].length; continue; }
    if ((m = NULL.exec(rest)))     { out.push({ kind: "null",   text: m[0] }); i += m[0].length; continue; }
    if ((m = PUNCT.exec(rest)))    { out.push({ kind: "punct",  text: m[0] }); i += m[0].length; continue; }
    // Inline key inside {}.
    const innerKey = rest.match(/^[-A-Za-z_][-A-Za-z0-9_]*\s*:/);
    if (innerKey) {
      const k = innerKey[0].replace(/\s*:$/, "");
      out.push({ kind: "key", text: k });
      out.push({ kind: "punct", text: ":" });
      i += innerKey[0].length;
      continue;
    }
    // Default: consume the next chunk until punct/space.
    const chunk = rest.match(/^[^\s,:{}\[\]'"]+/);
    const ch = chunk ? chunk[0] : rest[0] ?? "";
    out.push({ kind: "text", text: ch });
    i += ch.length;
  }
  return out;
}

export const TOKEN_CLASS: Record<TokenKind, string> = {
  key:       "text-foyr-deep font-semibold",
  string:    "text-fern",
  number:    "text-mulberry",
  bool:      "text-sea",
  null:      "text-sea italic",
  comment:   "text-ink-300 italic",
  anchor:    "text-mulberry",
  punct:     "text-ink-500",
  directive: "text-accent-dark",
  indent:    "",
  text:      "text-ink-950",
};
